import React from "react";
import { Link } from "react-router-dom";
import { SHIPPING_SETTINGS_PATH, parseWeightKg } from "../utils/shippingStatus";

/**
 * Edit product's Shipping Details (eventseasyadmin#299): the packed weight
 * and box size - for the product, or per value of one variant type (each
 * Size), or per combination of every variant - what customers pay for
 * shipping, and Cash on Delivery. Saved as Gift.shipping (uniquegiftapi
 * includes/productShipping.js). A courier store can't save a physical
 * product without its weights (weightProblem). Styling: AdminTheme.css .mtk-pship-*.
 */

// The variant type sizes come in, if the product has one
const SIZE_TYPE = /size|weight|pack|qty|quantity|volume/i;

export const defaultWeightType = (variants) => {
    const usable = (variants || []).filter((v) => v && v.type && (v.values || []).length);
    return (usable.find((v) => SIZE_TYPE.test(v.type)) || usable[0] || {}).type || "";
};

/** The rows to fill: [{ key, label }] - one for the product, or per variant value / combination */
export const weightRows = (variants, weightType, perCombination) => {
    const usable = (variants || []).filter((v) => v && v.type && (v.values || []).length);
    if (!usable.length || !weightType) return [{ key: "", label: "This product" }];
    if (perCombination) {
        const combos = usable.reduce((acc, v) => acc.flatMap((prefix) => v.values.map((value) => [...prefix, value])), [[]]);
        return combos.slice(0, 200).map((parts) => ({ key: parts.join(" / "), label: parts.join(" · ") }));
    }
    const type = usable.find((v) => v.type === weightType) || usable[0];
    return type.values.map((value) => ({ key: value, label: SIZE_TYPE.test(type.type) ? `${value} pack` : value }));
};

/** A stored weight ("0.27kg", "500g") as { amount, unit } for the inputs */
export const splitWeight = (stored) => {
    const kg = parseWeightKg(stored);
    if (kg == null) return { amount: "", unit: "kg" };
    const grams = /g$/i.test(String(stored).trim()) && !/kg$/i.test(String(stored).trim());
    return grams ? { amount: String(Math.round(kg * 1000 * 100) / 100), unit: "g" } : { amount: String(kg), unit: "kg" };
};

export const joinWeight = ({ amount, unit }) => (Number(amount) > 0 ? `${Number(amount)}${unit === "g" ? "g" : "kg"}` : "");

const emptyRow = () => ({ amount: "", unit: "kg", length: "", width: "", height: "" });

// Set on the form's rows when the product already has weights per variant
const HAS_VARIANT_WEIGHTS = Symbol("hasVariantWeights");

/**
 * One row of the form: its own, or - only for a product saved before weights
 * per variant (just shipping.weight and its box) - the product's, so re-saving
 * it (to change its price, say) doesn't wipe that weight. On a product with
 * weights per variant, a new variant stays blank and required: another
 * variant's weight would be wrong for it.
 */
export const rowFor = (rows, key) => rows[key] || (key !== "" && !rows[HAS_VARIANT_WEIGHTS] && rows[""]) || emptyRow();

/** True when these rows show the product's single weight on variants that have none of their own */
export const copiedFromProduct = (rowList, rows) => !rows[HAS_VARIANT_WEIGHTS]
    && Number((rows[""] || {}).amount) > 0
    && rowList.some(({ key }) => key !== "" && !rows[key]);

/** The form's rows from a saved product's shipping: { [key]: row } */
export const rowsFromShipping = (shipping = {}) => {
    const rows = {};
    for (const r of shipping.variant_weights || []) {
        rows[r.key] = { ...splitWeight(r.weight), length: r.dimensions?.length || "", width: r.dimensions?.width || "", height: r.dimensions?.height || "" };
    }
    rows[""] = { ...splitWeight(shipping.weight), length: shipping.dimensions?.length || "", width: shipping.dimensions?.width || "", height: shipping.dimensions?.height || "" };
    if ((shipping.variant_weights || []).length) rows[HAS_VARIANT_WEIGHTS] = true;
    return rows;
};

/**
 * The shipping weights to save: the product weight and box (the single row,
 * or the first variant's as a fallback for older readers) and the variant rows.
 */
export const weightsToSave = (rowList, rows, weightType, perCombination) => {
    const get = (key) => rowFor(rows, key);
    const dims = (r) => ({ length: Number(r.length) || 0, width: Number(r.width) || 0, height: Number(r.height) || 0 });
    if (rowList.length === 1 && rowList[0].key === "") {
        const r = get("");
        return { weight: joinWeight(r), dimensions: dims(r), weight_variant_type: "", weight_per_combination: false, variant_weights: [] };
    }
    const variant_weights = rowList.map(({ key }) => ({ key, weight: joinWeight(get(key)), dimensions: dims(get(key)) }));
    const first = variant_weights.find((r) => r.weight) || variant_weights[0];
    return {
        weight: first ? first.weight : "",
        dimensions: first ? first.dimensions : dims(emptyRow()),
        weight_variant_type: perCombination ? "" : weightType,
        weight_per_combination: !!perCombination,
        variant_weights,
    };
};

/** The first row without a weight, or null: a courier can't ship it */
export const weightProblem = (rowList, rows) => {
    const missing = rowList.find(({ key }) => !(Number(rowFor(rows, key).amount) > 0));
    return missing ? missing : null;
};

export default function ProductShippingDetails({
    variants, weightType, setWeightType, perCombination, setPerCombination, rows, setRows,
    freeShipping, setFreeShipping, chargeType, setChargeType, fixedCharge, setFixedCharge, ratePerKg, setRatePerKg,
    codAvailable, setCodAvailable, shipping, missingKey,
}) {
    const usable = (variants || []).filter((v) => v && v.type && (v.values || []).length);
    const rowList = weightRows(variants, weightType, perCombination);
    const others = usable.filter((v) => v.type !== weightType).map((v) => v.type.toLowerCase());
    const courierName = shipping.isCourier ? shipping.providerLabel : "";
    const required = shipping.state !== "unknown" && shipping.isCourier;
    // Off only when the store info says so (not while it's unknown or loading)
    const storeCodOff = shipping.state !== "unknown" && shipping.codEnabled === false;
    const missing = missingKey !== null && missingKey !== undefined ? rowList.find((r) => r.key === missingKey) : null;
    const copied = copiedFromProduct(rowList, rows);

    const setRow = (key, fields) => setRows((prev) => ({ ...prev, [key]: { ...emptyRow(), ...rowFor(prev, key), ...fields } }));

    return (
        <section className="mtk-pship" aria-labelledby="pship-title">
            <div className="mtk-pship__head">
                <div className="mtk-pship__title">
                    <span className="mtk-pship__icon" aria-hidden="true">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7h11v9H3zM14 10h4l3 3v3h-7" /><circle cx="7" cy="18" r="1.8" /><circle cx="17" cy="18" r="1.8" /></svg>
                    </span>
                    <div>
                        <h2 id="pship-title">Shipping Details</h2>
                        <div className="mtk-pship__sub">Weight and box size the courier uses to ship this product</div>
                    </div>
                </div>
                {required && (
                    <span className="mtk-pship__req">
                        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
                        Required for physical products
                    </span>
                )}
            </div>

            <div className="mtk-pship__body">
                {missing && (
                    <div className="mtk-pship__alert" role="alert">
                        <strong>Add a weight for {missing.key ? `the ${missing.label}` : "this product"} before saving.</strong> The courier can't create a shipment without it.
                    </div>
                )}

                {copied && (
                    <div className="mtk-pship__alert" role="status" data-testid="weights-copied">
                        <strong>Copied from the product's weight, please check each one.</strong> Different sizes usually weigh differently, and the courier charges by weight.
                    </div>
                )}

                <fieldset className="mtk-pship__weights">
                    <legend>Packed weight and box size</legend>
                    <p className="mtk-pship__help">
                        Weigh the product <strong>packed, with the box</strong>.
                        {usable.length > 0 && !perCombination && others.length > 0 && ` Set per ${weightType} – all ${others.join(" and ")} of the same ${weightType.toLowerCase()} use these values.`}
                        {usable.length > 0 && !perCombination && !others.length && ` Set per ${weightType}.`}
                    </p>
                    {usable.length > 1 && !perCombination && (
                        <label className="mtk-pship__inline">
                            Set weights per
                            <select className="form-control" value={weightType} onChange={(e) => setWeightType(e.target.value)}>
                                {usable.map((v) => <option key={v.type} value={v.type}>{v.type}</option>)}
                            </select>
                        </label>
                    )}
                    <div className="mtk-pship__table" role="table" aria-label="Packed weight and box size">
                        <div className="mtk-pship__row is-head" role="row">
                            <span role="columnheader">{rowList[0].key === "" ? "" : perCombination ? "Variant" : weightType}</span>
                            <span role="columnheader">Packed weight</span>
                            <span role="columnheader">Length (cm)</span>
                            <span role="columnheader">Width (cm)</span>
                            <span role="columnheader">Height (cm)</span>
                        </div>
                        {rowList.map(({ key, label }, i) => {
                            const r = { ...emptyRow(), ...rowFor(rows, key) };
                            const isMissing = missing && missing.key === key;
                            const id = `pship-${i}`;
                            return (
                                <div className={`mtk-pship__row${isMissing ? " is-missing" : ""}`} role="row" key={key || "product"}>
                                    <span role="cell" className="mtk-pship__label">{label}</span>
                                    <span role="cell">
                                        <span className="mtk-pship__weight">
                                            <label htmlFor={`${id}-w`} className="sr-only">Weight for {label}</label>
                                            <input id={`${id}-w`} className="form-control" inputMode="decimal" placeholder="0.00" value={r.amount}
                                                aria-invalid={isMissing || undefined}
                                                aria-describedby={isMissing ? `${id}-req` : undefined}
                                                onChange={(e) => setRow(key, { amount: e.target.value.replace(/[^\d.]/g, "") })} />
                                            <select className="form-control" aria-label={`Weight unit for ${label}`} value={r.unit} onChange={(e) => setRow(key, { unit: e.target.value })}>
                                                <option value="kg">kg</option>
                                                <option value="g">g</option>
                                            </select>
                                        </span>
                                        {isMissing && <span id={`${id}-req`} className="mtk-pship__req-text">Required</span>}
                                    </span>
                                    {["length", "width", "height"].map((d) => (
                                        <span role="cell" key={d}>
                                            <input className="form-control" inputMode="decimal" aria-label={`${d[0].toUpperCase()}${d.slice(1)} for ${label}`}
                                                value={r[d]} onChange={(e) => setRow(key, { [d]: e.target.value.replace(/[^\d.]/g, "") })} />
                                        </span>
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                    {usable.length > 1 && (
                        <label className="mtk-pship__check">
                            <input type="checkbox" checked={!!perCombination} onChange={(e) => setPerCombination(e.target.checked)} />
                            Different weight for each {others.join(" and ") || "variant"} too
                        </label>
                    )}
                </fieldset>

                <div>
                    <div className="mtk-pship__label-lg">Shipping charge</div>
                    <div className="mtk-pship__info">
                        <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><path d="M12 11v6M12 7h.01" /></svg>
                        <div>
                            <div className="font-weight-bold">
                                {courierName ? `You ship with ${courierName}` : shipping.provider === "manual" ? "You ship orders yourself" : "What customers pay for shipping"}
                            </div>
                            <div className="mtk-pship__help m-0">
                                {courierName ? `You pay ${courierName} for each shipment; customers pay the charge set below. ` : "Customers pay the charge set below. "}
                                Change how you ship in <Link to={SHIPPING_SETTINGS_PATH}>Shipping Settings</Link>.
                            </div>
                        </div>
                    </div>

                    <label className={`mtk-pship__switch${freeShipping ? " is-on" : ""}`}>
                        <input type="checkbox" role="switch" checked={freeShipping} onChange={(e) => setFreeShipping(e.target.checked)} />
                        <span className="mtk-switch__track" aria-hidden="true"><span /></span>
                        <span>
                            <span className="font-weight-bold">Offer free shipping on this product</span>
                            <span className="d-block mtk-pship__help m-0">
                                {freeShipping
                                    ? `On: customers see FREE delivery. You still pay ${courierName || "the courier"} for this shipment.`
                                    : "Customers see FREE delivery and you pay the courier yourself."}
                            </span>
                        </span>
                    </label>

                    {!freeShipping && (
                        <fieldset className="mtk-pship__charge">
                            <legend className="sr-only">Shipping charge</legend>
                            <label className="mtk-pship__radio">
                                <input type="radio" name="pship-charge" checked={chargeType !== "dynamic"} onChange={() => setChargeType("fixed")} />
                                Fixed per item
                            </label>
                            <label className="mtk-pship__radio">
                                <input type="radio" name="pship-charge" checked={chargeType === "dynamic"} onChange={() => setChargeType("dynamic")} />
                                By weight
                            </label>
                            {chargeType !== "dynamic" ? (
                                <label className="mtk-pship__inline">
                                    ₹
                                    <input className="form-control" type="number" min="0" max="9999" value={fixedCharge} placeholder="e.g. 49"
                                        aria-label="Shipping charge per item" onChange={(e) => setFixedCharge(e.target.value)} />
                                    per item
                                </label>
                            ) : (
                                <label className="mtk-pship__inline">
                                    ₹
                                    <input className="form-control" type="number" min="0" max="9999" value={ratePerKg} placeholder="e.g. 40"
                                        aria-label="Shipping charge per kg" onChange={(e) => setRatePerKg(e.target.value)} />
                                    per kg
                                </label>
                            )}
                        </fieldset>
                    )}
                </div>

                <div>
                    <div className="mtk-pship__label-lg">Cash on Delivery</div>
                    <label className={`mtk-pship__cod${storeCodOff ? " is-disabled" : ""}`}>
                        <input type="checkbox" checked={codAvailable} disabled={storeCodOff} onChange={(e) => setCodAvailable(e.target.checked)} />
                        <span>
                            <span className="font-weight-bold">Allow COD for this product</span>
                            <span className="d-block">
                                {storeCodOff
                                    ? <>COD is turned off for your whole store, so customers can't choose it. This product's choice applies again when you turn it on in <Link to={SHIPPING_SETTINGS_PATH}>Shipping Settings</Link>.</>
                                    : codAvailable
                                        ? "Customers can pay when the order arrives."
                                        : "Off: customers must pay online for this product (good for perishable items)."}
                            </span>
                        </span>
                    </label>
                </div>
            </div>
        </section>
    );
}
